// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// sim.ts — the colony.
//
// THE WHOLE RUN IS (mode, seed, commandLog). Nothing else enters. That single property is what makes
// the balance sim, the tutorial test, the mechanism audit and the shared daily seed all the same
// machinery, and it is why every quantity here is an integer, why there are five separate RNG
// streams, and why `setSpeed` is deliberately NOT a command: speed is presentation, and a test
// asserts that injecting arbitrary speed changes into a replay changes nothing.
//
// THE EVENT STREAM IS THE PRODUCT, not a debug aid. tests/helpers/audit.ts keeps its OWN ledger, its
// own flood-fill, its own Dijkstra and its own priority comparator, imports nothing from this file,
// and judges the run from `events` alone. An audit that checks the game against the game's own
// arithmetic is a tautology that passes on a completely broken game — which is exactly how a sibling
// in this fleet shipped turrets that could not shoot half the horde under 142 green tests.
//
// THE ORDER WITHIN A TICK IS FIXED AND LOAD-BEARING:
//   1 commands stamped at this tick, in log order
//   2 needs
//   3 job progress
//   4 movement
//   5 conversions (cook / build / clear / cut completing)
//   6 firepit burn
//   7 deaths
// Snow is not per tick (every 50), rooms and fields rebuild only on a cost change, illness rolls
// once a quarter, and job RE-SELECTION happens only at decision points — never per tick, which is
// the difference between a colony and a swarm of oscillating dots.

import {
  Build,
  BUILDS,
  CARRY_CAP,
  COLD_PER_TICK,
  COLD_RECOVERY,
  CUT_TICKS,
  EAT_AT,
  FORAGE_REGROW,
  FORAGE_TICKS,
  FORAGE_YIELD,
  HEALTH_DRAIN_COLD,
  HEALTH_DRAIN_HUNGER,
  HEALTH_RECOVERY,
  HUNGER_PER_TICK,
  ILLNESS_PERIOD,
  LOG_BURN,
  MAX_DEPTH,
  MEAL_IN_FOOD,
  MEAL_IN_WOOD,
  MEAL_OUT,
  MEAL_TICKS,
  MEAL_VALUE,
  MAX_STOCKPILES,
  NEED_MAX,
  Phase,
  REMEDY_IN_FOOD,
  REMEDY_IN_WOOD,
  REMEDY_TICKS,
  SLEEP_AT,
  SNOW_BATCH,
  STAND_REGROW,
  STAND_YIELD,
  STOCKPILE_CAP,
  TICKS_PER_DAY,
  TICKS_PER_QUARTER,
  TIRED_PER_TICK,
  TIRED_RECOVERY,
  TIRED_SLOW_AT,
  WARM_AT,
  COLD_OK,
  burnPeriod,
  clearTicks,
  mealsPerDay,
  modeOf,
  type Mode,
} from './modes';
import {
  COLS,
  CROSSING_ABANDON_ROW,
  TILES,
  Terrain,
  idx,
  manhattan,
  neighbours,
  rowOf,
  terrainOf,
} from './map';
import {
  GraphInput,
  INF,
  JAM_DEPTH,
  OUTDOOR,
  computeRooms,
  costField,
  isDoor,
  isJammed,
  passable,
  roomSize,
  stepCost,
  stepToward,
} from './graph';
import { makeSchedule, phaseAt, snowBatch } from './weather';
import { below, makeStreams, type Streams } from './streams';

// ── vocabulary ──────────────────────────────────────────────────────────────

export const enum Res {
  Wood = 0,
  Food = 1,
  Meal = 2,
  Remedy = 3,
}
export const RES_COUNT = 4;

/** The six job classes, in their DEFAULT priority order. The player permutes this. */
export const enum Cls {
  Haul = 0,
  Cook = 1,
  Clear = 2,
  Cut = 3,
  Build = 4,
  Tend = 5,
}
export const CLS_COUNT = 6;
export const CLS_NAMES = ['Haul', 'Cook', 'Clear', 'Cut', 'Build', 'Tend'] as const;

export const enum Skill {
  Cook = 0,
  Carpenter = 1,
  Forager = 2,
  Healer = 3,
}
export const SKILL_NAMES = ['cook', 'carpenter', 'forager', 'healer'] as const;

/** Causes of death, so the summary can name one. */
export const enum Cause {
  Hunger = 0,
  Cold = 1,
  Illness = 2,
}
export const CAUSE_NAMES = ['hunger', 'cold', 'illness'] as const;

export const enum Ev {
  Move = 0,
  Decision = 1,
  Claim = 2,
  Complete = 3,
  Fail = 4,
  Suspend = 5,
  Harvest = 6,
  Deposit = 7,
  Withdraw = 8,
  Craft = 9,
  Eat = 10,
  Burn = 11,
  Spill = 12,
  Heat = 13,
  Ill = 14,
  Recover = 15,
  Death = 16,
  Arrive = 17,
  PhaseChange = 18,
  Cleared = 19,
  Built = 20,
  Sever = 21,
  Restore = 22,
  /**
   * An input consumed by a craft or a build. It exists because the mechanism audit found the game
   * silently destroying resources: `craftAt` took its wood straight out of a store and `buildStep`
   * zeroed the carried wood when the building went up, both without emitting anything. The ledger
   * therefore did not balance, and nothing else in the game could ever have noticed — the numbers on
   * screen are read from the containers, which were correct. M1 is what noticed.
   */
  Consume = 23,
}

/** A flat six-number record. Cheap enough that 360 headless runs stay inside a minute. */
export interface Event {
  t: number;
  k: Ev;
  a: number;
  b: number;
  c: number;
  d: number;
}

export const enum Cmd {
  Designate = 0,
  Cancel = 1,
  Rank = 2,
  Forbid = 3,
  Call = 4,
  Arrival = 5,
}

export interface Command {
  tick: number;
  kind: Cmd;
  /** Designate/Cancel: a rectangle as [t0, t1]; Rank: the permutation; others: see the handler. */
  args: number[];
}

/** What a designation asks for. Build designations carry the Build id in `desigArg`. */
export const enum Desig {
  None = 0,
  Clear = 1,
  Cut = 2,
  Build = 3,
}

export interface Job {
  id: number;
  cls: Cls;
  /** Where the work happens. */
  tile: number;
  /** HAUL: the container index to lift from. BUILD: the Build id. Otherwise -1. */
  arg: number;
  claimedBy: number;
  failures: number;
  suspendedUntil: number;
  createdTick: number;
  done: boolean;
}

export interface Container {
  /** 0 stockpile, 1 ground pile. */
  kind: number;
  tile: number;
  cap: number;
  amt: Int32Array;
}

/** A colonist's current stage inside its claimed job. */
export const enum Stage {
  Idle = 0,
  Fetch = 1,
  Travel = 2,
  Work = 3,
  Deliver = 4,
}

export interface Alert {
  key: string;
  text: string;
  tile: number;
  tick: number;
  severity: number;
}

export interface State {
  mode: Mode;
  seed: number;
  tick: number;
  streams: Streams;
  schedule: Phase[];
  over: boolean;
  outcome: number; // 0 running, 1 overwintered, 2 lost

  terrain: Uint8Array;
  build: Uint8Array;
  depth: Uint8Array;
  fill: Int32Array;
  /** Tick a harvested stand/forage becomes available again; 0 means available now. */
  regrow: Int32Array;
  desig: Uint8Array;
  desigArg: Uint8Array;
  rooms: Int32Array;
  /** The rooms a firepit is warming right now. Recomputed each tick; read by the renderer. */
  warmRooms: Set<number>;
  /**
   * Per room, the tick its fire stops giving heat unless more wood arrives.
   *
   * Without this, a room with a firepit in it was warm whether or not any wood had ever reached the
   * fire — so the entire wood supply chain, which is half the game, had no effect on the thing that
   * actually kills colonists. The mechanism audit surfaced it as heat ticks vastly outnumbering
   * burns; the honest fix is that warmth expires.
   */
  warmUntil: Int32Array;
  /** Bumped whenever anything that changes walking cost changes. Fields cache against it. */
  costVersion: number;
  /** Traversals per tile — the run summary's chokepoint sentence, and nothing else. */
  traversal: Int32Array;

  // colonists, struct-of-arrays and iterated in id order, always
  n: number;
  name: string[];
  skillKind: Uint8Array;
  skillLvl: Uint8Array;
  pos: Int32Array;
  hunger: Int32Array;
  cold: Int32Array;
  tired: Int32Array;
  health: Int32Array;
  alive: Uint8Array;
  ill: Uint8Array;
  carryRes: Int32Array;
  carryN: Int32Array;
  job: Int32Array;
  stage: Uint8Array;
  stickyUntil: Int32Array;
  workLeft: Int32Array;
  moveUntil: Int32Array;
  moveTo: Int32Array;
  forbid: Int32Array;
  deathCause: Int32Array;
  /** A one-shot player order that outranks the queue. -1 when none. */
  callTo: Int32Array;
  hauled: Int32Array;
  walked: Int32Array;

  cont: Container[];
  jobs: Job[];
  nextJobId: number;
  rank: number[];

  events: Event[];
  alerts: Alert[];
  arrivalsSeen: number;
  pendingArrival: number;

  // tallies for the summary, all derived from the same transactions the audit reads
  produced: Int32Array;
  eaten: number;
  buried: number;
  burned: number;
  severedTicks: number;
  worstSeverance: number;
  severOpen: number;
  deaths: number;
}

// ── construction ────────────────────────────────────────────────────────────

const NAMES = [
  'Bregg', 'Maret', 'Ondi', 'Silla', 'Halvor', 'Kess', 'Tove', 'Rask', 'Ilma', 'Dorn',
  'Wren', 'Sten', 'Ava', 'Juri', 'Nel', 'Orm',
];

export function createState(seed: number, modeId: string): State {
  const mode = modeOf(modeId);
  const streams = makeStreams(seed);
  const terrain = terrainOf(mode.map);

  // Density thins the map's resources deterministically, in tile order.
  if (mode.density < 100) {
    for (let t = 0; t < TILES; t++) {
      if (terrain[t] !== Terrain.Stand && terrain[t] !== Terrain.Forage) continue;
      if (below(streams.forage, 100) >= mode.density) terrain[t] = Terrain.Open;
    }
  }

  const s: State = {
    mode,
    seed,
    tick: 0,
    streams,
    schedule: makeSchedule(streams.weather, mode.days),
    over: false,
    outcome: 0,
    terrain,
    build: new Uint8Array(TILES),
    depth: new Uint8Array(TILES),
    fill: new Int32Array(TILES),
    regrow: new Int32Array(TILES),
    desig: new Uint8Array(TILES),
    desigArg: new Uint8Array(TILES),
    rooms: new Int32Array(TILES),
    warmRooms: new Set<number>(),
    warmUntil: new Int32Array(TILES),
    costVersion: 1,
    traversal: new Int32Array(TILES),
    n: 0,
    name: [],
    skillKind: new Uint8Array(16),
    skillLvl: new Uint8Array(16),
    pos: new Int32Array(16),
    hunger: new Int32Array(16),
    cold: new Int32Array(16),
    tired: new Int32Array(16),
    health: new Int32Array(16),
    alive: new Uint8Array(16),
    ill: new Uint8Array(16),
    carryRes: new Int32Array(16),
    carryN: new Int32Array(16),
    job: new Int32Array(16),
    stage: new Uint8Array(16),
    stickyUntil: new Int32Array(16),
    workLeft: new Int32Array(16),
    moveUntil: new Int32Array(16),
    moveTo: new Int32Array(16),
    forbid: new Int32Array(16),
    deathCause: new Int32Array(16),
    callTo: new Int32Array(16),
    hauled: new Int32Array(16),
    walked: new Int32Array(16),
    cont: [],
    jobs: [],
    nextJobId: 1,
    rank: [Cls.Haul, Cls.Cook, Cls.Clear, Cls.Cut, Cls.Build, Cls.Tend],
    events: [],
    alerts: [],
    arrivalsSeen: 0,
    pendingArrival: -1,
    produced: new Int32Array(RES_COUNT),
    eaten: 0,
    buried: 0,
    burned: 0,
    severedTicks: 0,
    worstSeverance: 0,
    severOpen: 0,
    deaths: 0,
  };
  s.job.fill(-1);
  s.callTo.fill(-1);
  s.carryRes.fill(-1);
  // -1 means "not mid-step". A zero-filled moveTo reads as "arriving at tile 0", and advance() duly
  // teleported the entire colony to the top-left corner of the map on the very first tick — with
  // every unit test green, because nothing in the rules cares where a colonist starts. The balance
  // sim caught it as "The Crossing produces zero wood and nobody ever eats".
  s.moveTo.fill(-1);

  layout(s);
  for (let i = 0; i < mode.souls; i++) addColonist(s, i);
  refreshGraph(s);
  openingCrisis(s);
  // The fires are already lit when the run opens, and warmRooms is populated before the first tick —
  // otherwise the very first frame, which is the one a paused tutorial sits on for as long as the
  // player reads it, shows a house with no fire in it.
  for (const room of firepitRooms(s)) {
    s.warmUntil[room] = burnPeriod(roomSize(s.rooms, room), false) * LOG_BURN;
  }
  heatedRooms(s);
  return s;
}

/**
 * The starting shelter, and the tile the whole opening is about.
 *
 * The shelter is a walled room with ONE door, and the larder sits OUTSIDE it, two tiles up the
 * corridor. That is not decoration: it is what makes the first thing the player ever sees a supply
 * route with a drift already growing across it.
 */
const DOOR_T = idx(5, 8);
const CORRIDOR_T = idx(5, 7);
const LARDER_T = idx(5, 6);

function layout(s: State): void {
  const wall = (c: number, r: number) => {
    s.build[idx(c, r)] = Build.Wall + 1;
  };
  const put = (c: number, r: number, b: Build) => {
    s.build[idx(c, r)] = b + 1;
  };

  if (s.mode.map === 'home') {
    for (let c = 3; c <= 8; c++) {
      wall(c, 8);
      wall(c, 12);
    }
    for (let r = 9; r <= 11; r++) {
      wall(3, r);
      wall(8, r);
    }
    put(5, 8, Build.Door);
    put(4, 9, Build.Firepit);
    put(6, 9, Build.Stove);
    put(4, 10, Build.Bed);
    put(5, 10, Build.Bed);
    put(6, 10, Build.Bed);
    put(7, 10, Build.Bed);
    put(4, 11, Build.Bed);
    put(5, 11, Build.Bed);
    // TWO STORES, AND THE SPLIT IS THE WHOLE OPENING. The hearth store inside holds the cooked
    // meals; the larder outside, up the corridor, holds the bulk wood and food. So a corridor that
    // plugs is a slow squeeze — the colony eats what is in the house while the fire runs out of wood
    // — rather than a guillotine. With a single store outside the walls the first drift stranded
    // whoever happened to be standing at it, and every seed ended with a frozen colonist on the wrong
    // side of one tile inside four minutes.
    put(7, 9, Build.Stockpile);
    put(5, 6, Build.Stockpile);
  } else {
    // The Crossing. A SOUND longhouse north of the neck — walls, beds, fire, stove and an empty
    // stockpile — and a ruined camp south of it holding everything you actually own.
    //
    // The longhouse is furnished on purpose. An earlier version left the north bare and demanded the
    // colony BUILD a stove up there before the fourth day, which turned a mode about moving a colony
    // into a mode about construction logistics under a deadline, and no policy ever finished it. The
    // mode's question is "can you get your winter through a four-tile neck in time", so everything
    // that is not carrying is scaffolding.
    for (let c = 3; c <= 8; c++) {
      wall(c, 0);
      wall(c, 4);
    }
    for (let r = 1; r <= 3; r++) {
      wall(3, r);
      wall(8, r);
    }
    put(5, 4, Build.Door);
    put(4, 1, Build.Firepit);
    put(6, 1, Build.Stove);
    put(7, 1, Build.Stockpile);
    put(4, 2, Build.Bed);
    put(5, 2, Build.Bed);
    put(6, 2, Build.Bed);
    put(7, 2, Build.Bed);
    put(4, 3, Build.Bed);
    // The south camp: open ground, no walls left, and the stores that are about to go under.
    put(5, 10, Build.Stockpile);
    put(4, 10, Build.Stove);
    put(6, 10, Build.Firepit);
    put(3, 11, Build.Bed);
    put(4, 11, Build.Bed);
  }

  // Every stockpile placed above becomes a container, in tile order so ids are stable.
  for (let t = 0; t < TILES; t++) {
    if (s.build[t] === Build.Stockpile + 1) s.cont.push(makeCont(0, t));
  }
  // Seed the stores BY TILE, never by index. Containers are pushed in tile order, so on The Crossing
  // the northern longhouse store sorts first and an index-based seeding put the colony's entire
  // winter at the destination — which is the one place the mode is about it NOT being.
  const seed = (tile: number, wood: number, food: number, meal: number) => {
    const c = s.cont.find((x) => x.tile === tile);
    if (!c) return;
    c.amt[Res.Wood] += wood;
    c.amt[Res.Food] += food;
    c.amt[Res.Meal] += meal;
  };
  if (s.mode.map === 'home') {
    // A woodpile by the hearth and the bulk outside. The split is the opening: the fire has a few
    // days in hand, and the corridor to the larder is what decides whether it gets any more.
    seed(idx(5, 6), s.mode.startWood, s.mode.startFood, 0);
    seed(idx(7, 9), 10, 0, s.mode.startMeals);
  } else {
    // Everything you own is in the south, which is the half that goes under on the fourth day.
    seed(idx(5, 10), s.mode.startWood, s.mode.startFood, s.mode.startMeals);
  }
}

function makeCont(kind: number, tile: number): Container {
  return { kind, tile, cap: kind === 0 ? STOCKPILE_CAP : 1 << 20, amt: new Int32Array(RES_COUNT) };
}

function addColonist(s: State, i: number): void {
  const r = s.streams.names;
  s.n = Math.max(s.n, i + 1);
  s.name[i] = NAMES[i % NAMES.length];
  // Skills are dealt so the FIRST four colonists cover all four trades exactly once. A colony whose
  // only cook depends on a lucky roll is a colony whose opening is a lottery.
  s.skillKind[i] = i < 4 ? i : below(r, 4);
  s.skillLvl[i] = i < 4 ? 2 + below(r, 2) : 1 + below(r, 2);
  // Placed on a tile that is actually free, not on an arithmetic grid. The grid version put
  // Fastness's ninth colonist at (4,12) — inside the shelter's south wall — where they were
  // unreachable from everything and simply starved, on every seed, invisibly.
  s.pos[i] = spawnTile(s, i);
  s.hunger[i] = 1200 + below(r, 900);
  s.cold[i] = 500 + below(r, 400);
  s.tired[i] = 800 + below(r, 900);
  s.health[i] = NEED_MAX;
  s.alive[i] = 1;
  s.job[i] = -1;
  s.callTo[i] = -1;
  s.carryRes[i] = -1;
}

/**
 * The nth free standing-place, searched outward from the hearth in tile order so it is deterministic
 * and so a big roster spills into the ground just outside the door rather than into a wall.
 */
function spawnTile(s: State, n: number): number {
  const free: number[] = [];
  const g = graphOf(s);
  const start = s.mode.map === 'home' ? idx(4, 10) : idx(3, 10);
  for (let t = 0; t < TILES; t++) {
    if (!passable(g, t)) continue;
    if (s.build[t] === Build.Stove + 1 || s.build[t] === Build.Firepit + 1) continue;
    free.push(t);
  }
  free.sort((a, b) => manhattan(a, start) - manhattan(b, start) || a - b);
  return free.length > 0 ? free[n % free.length] : start;
}

/**
 * THE RUN OPENS MID-CRISIS. There is no empty-lot phase and no build-up minute: a drift is already
 * two thirds of the way across the one tile joining the shelter to the larder, and one colonist is
 * already hungry enough to matter. The player's first action is a repair, which is the only reliable
 * way a colony sim is interesting inside its first minute.
 */
function openingCrisis(s: State): void {
  // Depth ONE, not two. At depth 2 with a part-filled accumulator the corridor reached depth 3 —
  // impassable — inside about 250 ticks, twenty-five seconds, before a first-time player has finished
  // reading the HUD. Depth 1 gives roughly seventy-five seconds: long enough to notice, understand
  // and act, short enough that ignoring it costs the run.
  const t = s.mode.map === 'home' ? CORRIDOR_T : idx(5, 6);
  s.depth[t] = 1;
  s.fill[t] = 30;
  s.hunger[0] = 7100;
  s.tired[1] = 6400;
  s.costVersion++;
}

// ── events ──────────────────────────────────────────────────────────────────

function emit(s: State, k: Ev, a = 0, b = 0, c = 0, d = 0): void {
  s.events.push({ t: s.tick, k, a, b, c, d });
}

// ── the graph, rebuilt only when walking cost changed ────────────────────────

const FIELD_CACHE = new Map<number, { v: number; f: Int32Array }>();
let cacheOwner: State | null = null;

function graphOf(s: State): GraphInput {
  return { terrain: s.terrain, build: s.build, depth: s.depth };
}

function refreshGraph(s: State): void {
  computeRooms(graphOf(s), s.rooms);
}

/**
 * A cost field grown from `source`, cached against `costVersion`. The cache is keyed by tile, so it
 * holds at most one entry per colonist plus one per store — which is why the stockpile cap is a game
 * rule and not a suggestion.
 */
export function fieldFrom(s: State, source: number): Int32Array {
  if (cacheOwner !== s) {
    FIELD_CACHE.clear();
    cacheOwner = s;
  }
  const hit = FIELD_CACHE.get(source);
  if (hit && hit.v === s.costVersion) return hit.f;
  const f = hit ? hit.f : new Int32Array(TILES);
  costField(graphOf(s), source, f);
  FIELD_CACHE.set(source, { v: s.costVersion, f });
  return f;
}

/** Bump when anything that changes walking cost changed, and re-derive rooms. */
function costChanged(s: State): void {
  s.costVersion++;
  refreshGraph(s);
}

// ── the ledger ──────────────────────────────────────────────────────────────

function contAt(s: State, tile: number): number {
  for (let i = 0; i < s.cont.length; i++) if (s.cont[i].tile === tile) return i;
  return -1;
}

function total(c: Container): number {
  let n = 0;
  for (let r = 0; r < RES_COUNT; r++) n += c.amt[r];
  return n;
}

function deposit(s: State, who: number, ci: number, res: Res, n: number): void {
  s.cont[ci].amt[res] += n;
  emit(s, Ev.Deposit, who, res, n, ci);
}

function withdraw(s: State, who: number, ci: number, res: Res, n: number): boolean {
  if (s.cont[ci].amt[res] < n) return false;
  s.cont[ci].amt[res] -= n;
  s.carryRes[who] = res;
  s.carryN[who] += n;
  emit(s, Ev.Withdraw, who, res, n, ci);
  return true;
}

/** A ground pile at depth >= 2 is BURIED — still in the ledger, simply not a haul candidate. */
function buried(s: State, ci: number): boolean {
  return s.cont[ci].kind === 1 && s.depth[s.cont[ci].tile] >= JAM_DEPTH;
}

function spill(s: State, who: number, res: Res, n: number): void {
  const tile = s.pos[who];
  let ci = contAt(s, tile);
  if (ci < 0 || s.cont[ci].kind !== 1) {
    s.cont.push(makeCont(1, tile));
    ci = s.cont.length - 1;
  }
  s.cont[ci].amt[res] += n;
  emit(s, Ev.Spill, who, res, n, tile);
}

/** The nearest reachable container holding `res`, by walking cost, ties broken by container index. */
function nearestWith(s: State, from: number, res: Res, n: number): number {
  const f = fieldFrom(s, from);
  let best = -1;
  let bestD = INF;
  for (let i = 0; i < s.cont.length; i++) {
    const c = s.cont[i];
    if (c.amt[res] < n) continue;
    if (buried(s, i)) continue;
    const d = f[c.tile];
    if (d >= INF) continue;
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

function nearestStoreWithSpace(s: State, from: number): number {
  const f = fieldFrom(s, from);
  let best = -1;
  let bestD = INF;
  for (let i = 0; i < s.cont.length; i++) {
    const c = s.cont[i];
    if (c.kind !== 0) continue;
    if (total(c) >= c.cap) continue;
    const d = f[c.tile];
    if (d >= INF) continue;
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

/** Every unit of a resource anywhere in a reachable store. Drives the HUD and the days-left pill. */
export function stockOf(s: State, res: Res): number {
  let n = 0;
  for (let i = 0; i < s.cont.length; i++) if (!buried(s, i)) n += s.cont[i].amt[res];
  return n;
}

export function reachableStock(s: State, res: Res, from: number): number {
  const f = fieldFrom(s, from);
  let n = 0;
  for (let i = 0; i < s.cont.length; i++) {
    if (buried(s, i)) continue;
    if (f[s.cont[i].tile] >= INF) continue;
    n += s.cont[i].amt[res];
  }
  return n;
}

/**
 * Days of cover, ANALYTIC — never a sample. See the plan for why the measured form was worse.
 *
 * It counts cooked meals PLUS the meals the raw food in store could still become, because that is
 * what "how long can we eat" actually means. Counting only cooked meals made the headline number
 * read 0.7 days at the start of every run — technically true, wildly misleading, and the single most
 * prominent thing on the screen. A player would have read the opening as already lost.
 */
export function daysOfMeals(s: State): number {
  const souls = livingCount(s);
  if (souls === 0) return 0;
  const cookable = ((stockOf(s, Res.Food) / MEAL_IN_FOOD) | 0) * MEAL_OUT;
  return (stockOf(s, Res.Meal) + cookable) / mealsPerDay(souls);
}

/**
 * Days of COOKED food only. The HUD figure above deliberately counts raw food as well, because that
 * is what "how long can we eat" means to a player — but the cook's own stop-condition must not, or
 * the colony looks well fed precisely because nobody has cooked the food yet, and then starves with
 * a full larder. That regression was introduced by the HUD fix and caught by the balance sim in one
 * run: meals produced fell from 83 to 8.
 */
export function daysOfCooked(s: State): number {
  const souls = livingCount(s);
  if (souls === 0) return 0;
  return stockOf(s, Res.Meal) / mealsPerDay(souls);
}

export function livingCount(s: State): number {
  let n = 0;
  for (let i = 0; i < s.n; i++) if (s.alive[i]) n++;
  return n;
}

function firstFirepit(s: State): number {
  for (let t = 0; t < TILES; t++) if (s.build[t] === Build.Firepit + 1) return t;
  return -1;
}

function homeTile(s: State): number {
  return s.cont.length > 0 ? s.cont[0].tile : firstFirepit(s);
}

// ── jobs ────────────────────────────────────────────────────────────────────

function addJob(s: State, cls: Cls, tile: number, arg: number): void {
  for (const j of s.jobs) if (!j.done && j.cls === cls && j.tile === tile && j.arg === arg) return;
  s.jobs.push({
    id: s.nextJobId++,
    cls,
    tile,
    arg,
    claimedBy: -1,
    failures: 0,
    suspendedUntil: 0,
    createdTick: s.tick,
    done: false,
  });
}

/**
 * Rebuild the automatic jobs. Player designations persist as tiles and are turned into jobs here, so
 * a designation on a tile that becomes unreachable simply stops being a candidate — which is what
 * lets a severed wing quietly stop being served instead of paralysing the colony. That silence is
 * the drama; the alert rail is what stops it being a mystery.
 */
function regenJobs(s: State): void {
  for (const j of s.jobs) {
    if (j.done) continue;
    if (j.cls === Cls.Clear && s.depth[j.tile] === 0) j.done = true;
    if (j.cls === Cls.Cut && s.terrain[j.tile] !== Terrain.Stand && s.terrain[j.tile] !== Terrain.Forage) j.done = true;
  }
  s.jobs = s.jobs.filter((j) => !j.done || j.claimedBy >= 0);

  for (let t = 0; t < TILES; t++) {
    const d = s.desig[t];
    if (d === Desig.Clear && s.depth[t] > 0) addJob(s, Cls.Clear, t, -1);
    else if (d === Desig.Cut && (s.terrain[t] === Terrain.Stand || s.terrain[t] === Terrain.Forage) && s.regrow[t] <= s.tick) {
      addJob(s, Cls.Cut, t, -1);
    } else if (d === Desig.Build && s.build[t] === 0) addJob(s, Cls.Build, t, s.desigArg[t]);
  }

  // A DOORWAY DIGS ITSELF OUT, because a colony that watches its own front door drift shut and does
  // nothing is not a colony. Snow falls on a door tile by rule (it is a threshold people keep
  // opening), and at depth 2 the door can no longer be pulled shut, so the room stops being enclosed
  // and the fire stops warming anybody. Before this existed the door jammed inside two days on every
  // seed and the entire fleet froze with a full larder.
  //
  // It is a JOB, not an exemption: it costs a colonist and it sits in the CLEAR class, so a player
  // who buries CLEAR at the bottom of the work board really will find the house has gone cold. That
  // is the lesson; an unrecoverable jam was just a bug.
  for (let t = 0; t < TILES; t++) {
    if (isDoor(s.build, t) && s.depth[t] > 0) addJob(s, Cls.Clear, t, -1);
  }

  // HAUL: a ground pile that is not buried, AND somewhere with space to put it.
  //
  // The space test is what closes an infinite loop the balance sim found on its first run: with every
  // store full, a hauler lifted a pile, failed to find anywhere to put it, spilled it back on the
  // same tile, which created the same job again — 800 spills in a twelve-day run, and every other
  // assertion green throughout. A job that cannot be completed must not exist.
  const anySpace = s.cont.some((c) => c.kind === 0 && total(c) < c.cap);
  if (anySpace) {
    for (let i = 0; i < s.cont.length; i++) {
      if (s.cont[i].kind !== 1) continue;
      if (buried(s, i)) continue;
      if (total(s.cont[i]) <= 0) continue;
      addJob(s, Cls.Haul, s.cont[i].tile, i);
    }
  }

  // COOK: a stove, ingredients somewhere, and not already swimming in meals.
  const stove = stoveTile(s);
  if (stove >= 0 && daysOfCooked(s) < 3) {
    if (stockOf(s, Res.Food) >= MEAL_IN_FOOD && stockOf(s, Res.Wood) >= MEAL_IN_WOOD) {
      addJob(s, Cls.Cook, stove, -1);
    }
  }

  // TEND: somebody is ill. Deliver a remedy if one exists; otherwise make one, at the same stove,
  // out of the same food dinner wants. That contention is deliberate and it is interaction C.
  let anyIll = false;
  for (let i = 0; i < s.n; i++) if (s.alive[i] && s.ill[i]) anyIll = true;
  if (anyIll) {
    if (stockOf(s, Res.Remedy) > 0) {
      for (let i = 0; i < s.n; i++) if (s.alive[i] && s.ill[i]) addJob(s, Cls.Tend, s.pos[i], i);
    } else if (stove >= 0 && stockOf(s, Res.Food) >= REMEDY_IN_FOOD && stockOf(s, Res.Wood) >= REMEDY_IN_WOOD) {
      addJob(s, Cls.Tend, stove, -2);
    }
  }
}

function stoveTile(s: State): number {
  for (let t = 0; t < TILES; t++) if (s.build[t] === Build.Stove + 1) return t;
  return -1;
}

export function rankOf(s: State, cls: Cls): number {
  return s.rank.indexOf(cls);
}

/**
 * THE SELECTION RULE, and the single most load-bearing function in the game.
 *
 * At a decision point a colonist takes the lexicographic minimum of (classRank, pathCost, jobId)
 * over jobs that are unclaimed, unforbidden, not suspended and REACHABLE. Nothing else. M2 re-derives
 * exactly this from its own cost field and its own comparator and asserts the claim matched.
 */
function chooseJob(s: State, who: number): number {
  const f = fieldFrom(s, s.pos[who]);
  let best = -1;
  let bestRank = 1 << 20;
  let bestCost = INF;
  for (const j of s.jobs) {
    if (j.done || j.claimedBy >= 0) continue;
    if (j.suspendedUntil > s.tick) continue;
    if (s.forbid[who] & (1 << j.cls)) continue;
    const d = f[j.tile];
    if (d >= INF) continue;
    const r = rankOf(s, j.cls);
    if (r < bestRank || (r === bestRank && (d < bestCost || (d === bestCost && best >= 0 && j.id < best)))) {
      if (r < bestRank || d < bestCost || j.id < best) {
        bestRank = r;
        bestCost = d;
        best = j.id;
      }
    }
  }
  return best;
}

function countCandidates(s: State, who: number): number {
  const f = fieldFrom(s, s.pos[who]);
  let n = 0;
  for (const j of s.jobs) {
    if (j.done || j.claimedBy >= 0) continue;
    if (j.suspendedUntil > s.tick) continue;
    if (s.forbid[who] & (1 << j.cls)) continue;
    if (f[j.tile] < INF) n++;
  }
  return n;
}

function jobById(s: State, id: number): Job | undefined {
  for (const j of s.jobs) if (j.id === id) return j;
  return undefined;
}

function release(s: State, who: number, failed: boolean): void {
  const id = s.job[who];
  if (id >= 0) {
    const j = jobById(s, id);
    if (j) {
      j.claimedBy = -1;
      if (failed) {
        j.failures++;
        emit(s, Ev.Fail, who, j.id, j.cls, 0);
        if (j.failures >= 3) {
          j.suspendedUntil = s.tick + 100;
          j.failures = 0;
          emit(s, Ev.Suspend, j.id, j.cls, j.tile, 0);
          raise(s, `sus${j.id}`, `${CLS_NAMES[j.cls]} at that tile keeps failing`, j.tile, 2);
        }
      }
    }
  }
  s.job[who] = -1;
  s.stage[who] = Stage.Idle;
  s.workLeft[who] = 0;
}

// ── alerts ──────────────────────────────────────────────────────────────────

function raise(s: State, key: string, text: string, tile: number, severity: number): void {
  for (const a of s.alerts) if (a.key === key) return;
  s.alerts.push({ key, text, tile, tick: s.tick, severity });
  if (s.alerts.length > 24) s.alerts.shift();
}

function clearAlert(s: State, key: string): void {
  s.alerts = s.alerts.filter((a) => a.key !== key);
}

// ── movement ────────────────────────────────────────────────────────────────

function beginStep(s: State, who: number, to: number): boolean {
  const cost = stepCost(graphOf(s), to);
  if (cost >= INF) return false;
  s.moveTo[who] = to;
  s.moveUntil[who] = s.tick + cost;
  emit(s, Ev.Move, who, s.pos[who], to, s.depth[to]);
  return true;
}

function walkToward(s: State, who: number, dest: number): boolean {
  if (s.pos[who] === dest) return true;
  const f = fieldFrom(s, dest);
  if (f[s.pos[who]] >= INF) return false;
  const next = stepToward(graphOf(s), f, s.pos[who]);
  if (next < 0) return false;
  return beginStep(s, who, next);
}

function adjacentOrOn(s: State, who: number, tile: number): boolean {
  if (s.pos[who] === tile) return true;
  const buf = new Int32Array(4);
  const n = neighbours(tile, buf);
  for (let i = 0; i < n; i++) if (buf[i] === s.pos[who]) return true;
  return false;
}

// ── the tick ────────────────────────────────────────────────────────────────

const SNOW_SCRATCH = new Uint8Array(TILES);

export function step(s: State, commands: readonly Command[] = []): void {
  if (s.over) return;

  // 1 — commands stamped at this tick, in log order
  for (const c of commands) applyCommand(s, c);

  const phase = phaseAt(s.schedule, s.tick, TICKS_PER_QUARTER);
  const prevPhase = s.tick === 0 ? phase : phaseAt(s.schedule, s.tick - 1, TICKS_PER_QUARTER);
  if (phase !== prevPhase) {
    emit(s, Ev.PhaseChange, phase, 0, 0, 0);
    if (phase === Phase.Storm) raise(s, `storm${s.tick}`, 'A storm has begun', -1, 3);
  }

  // 2 — needs
  needs(s, phase);

  // 3, 4, 5 — each colonist advances its claimed job, in id order, always
  for (let i = 0; i < s.n; i++) {
    if (!s.alive[i]) continue;
    advance(s, i);
  }

  // 6 — the fire
  if (s.tick % 10 === 0) burnFuel(s, phase);

  // snow, on its own cadence
  if (s.tick > 0 && s.tick % SNOW_BATCH === 0) {
    snowBatch(snowWorld(s), s.depth, s.fill, phase, SNOW_SCRATCH);
    costChanged(s);
    checkSeverance(s);
  }

  if (s.tick % ILLNESS_PERIOD === 0 && s.tick > 0) rollIllness(s);
  if (s.tick % 25 === 0) regenJobs(s);
  if (s.tick % TICKS_PER_DAY === 0 && s.tick > 0) dayBreak(s);
  if (s.mode.abandonDay > 0 && s.tick === s.mode.abandonDay * TICKS_PER_DAY + TICKS_PER_QUARTER * 2) {
    abandonTheSouth(s);
  }

  // 7 — deaths already applied inside needs(); decide whether the winter is over
  s.tick++;
  if (s.tick >= s.mode.days * TICKS_PER_DAY) {
    s.over = true;
    s.outcome = livingCount(s) > 0 ? 1 : 2;
  } else if (livingCount(s) === 0) {
    s.over = true;
    s.outcome = 2;
  }
}

function snowWorld(s: State) {
  return {
    accumulates(t: number): boolean {
      if (s.terrain[t] === Terrain.Rock) return false;
      if (s.build[t] === Build.Wall + 1) return false;
      // A door is a threshold people keep opening, so it always takes snow. Without this the jam is
      // circular — a door is indoors, so it never accumulates, so it can never jam.
      if (isDoor(s.build, t)) return true;
      return s.rooms[t] === OUTDOOR;
    },
    obstructs(t: number): boolean {
      if (s.terrain[t] === Terrain.Rock) return true;
      if (s.build[t] === Build.Wall + 1) return true;
      if (isDoor(s.build, t)) return !isJammed(s.build, s.depth, t);
      return false;
    },
    isPath(t: number): boolean {
      return s.build[t] === Build.Path + 1;
    },
  };
}

function needs(s: State, phase: Phase): void {
  const heated = heatedRooms(s);
  for (let i = 0; i < s.n; i++) {
    if (!s.alive[i]) continue;
    s.hunger[i] = Math.min(NEED_MAX, s.hunger[i] + HUNGER_PER_TICK);

    const warm = heated.has(s.rooms[s.pos[i]]) && s.rooms[s.pos[i]] !== OUTDOOR;
    s.cold[i] = warm
      ? Math.max(0, s.cold[i] - COLD_RECOVERY)
      : Math.min(NEED_MAX, s.cold[i] + COLD_PER_TICK + (phase === Phase.Storm ? 2 : 0));

    const inBed = s.build[s.pos[i]] === Build.Bed + 1 && s.stage[i] === Stage.Idle && s.tired[i] > SLEEP_AT / 2;
    s.tired[i] = inBed
      ? Math.max(0, s.tired[i] - TIRED_RECOVERY)
      : Math.min(NEED_MAX, s.tired[i] + TIRED_PER_TICK);

    let drain = 0;
    if (s.hunger[i] >= NEED_MAX) drain += HEALTH_DRAIN_HUNGER;
    if (s.cold[i] >= NEED_MAX) drain += HEALTH_DRAIN_COLD;
    if (s.ill[i]) drain += 2;
    if (drain > 0) {
      s.health[i] -= drain;
      if (s.health[i] <= 0) {
        s.health[i] = 0;
        s.alive[i] = 0;
        s.deathCause[i] = s.ill[i] ? Cause.Illness : s.cold[i] >= NEED_MAX ? Cause.Cold : Cause.Hunger;
        s.deaths++;
        release(s, i, false);
        emit(s, Ev.Death, i, s.deathCause[i], 0, 0);
        raise(s, `death${i}`, `${s.name[i]} has died of ${CAUSE_NAMES[s.deathCause[i]]}`, s.pos[i], 4);
      }
    } else if (s.health[i] < NEED_MAX && !s.ill[i] && s.hunger[i] < EAT_AT && s.cold[i] < NEED_MAX / 2) {
      s.health[i] = Math.min(NEED_MAX, s.health[i] + HEALTH_RECOVERY);
    }
  }
}

/**
 * Which rooms have a firepit in them. A ROOM, exactly — never a radius, and never the outdoors.
 *
 * That last clause is not a detail: without it a firepit lit in an open field "heats" the entire
 * outdoor component, which is the whole map, and the cold system stops existing. M6 re-derives this
 * set from its own independent flood-fill and asserts the heated set is exactly the component.
 */
function heatedRooms(s: State): Set<number> {
  s.warmRooms.clear();
  for (let t = 0; t < TILES; t++) {
    if (s.build[t] !== Build.Firepit + 1) continue;
    const room = s.rooms[t];
    if (room === OUTDOOR) continue;
    // A fire that has not been fed has gone out. This is what connects the wood supply chain to the
    // thing that actually kills people.
    if (s.warmUntil[room] <= s.tick) continue;
    s.warmRooms.add(room);
  }
  return s.warmRooms;
}

/** Rooms with a firepit, fuelled or not — the set burnFuel tries to feed. */
function firepitRooms(s: State): number[] {
  const out: number[] = [];
  for (let t = 0; t < TILES; t++) {
    if (s.build[t] !== Build.Firepit + 1) continue;
    const room = s.rooms[t];
    if (room === OUTDOOR) continue;
    if (!out.includes(room)) out.push(room);
  }
  return out.sort((a, b) => a - b);
}

function burnFuel(s: State, phase: Phase): void {
  for (const room of firepitRooms(s)) {
    const size = roomSize(s.rooms, room);
    const period = burnPeriod(size, phase === Phase.Storm);
    // Feed the fire a little before it goes out, so a well-supplied room never flickers.
    if (s.warmUntil[room] > s.tick + 10) continue;
    const ci = nearestWith(s, firepitOfRoom(s, room), Res.Wood, 1);
    if (ci < 0) {
      raise(s, `nofuel${room}`, 'No wood within reach of the fire', firepitOfRoom(s, room), 3);
      continue;
    }
    clearAlert(s, `nofuel${room}`);
    s.cont[ci].amt[Res.Wood] -= 1;
    s.burned++;
    s.warmUntil[room] = s.tick + period * LOG_BURN;
    emit(s, Ev.Burn, ci, 1, room, size);
    emit(s, Ev.Heat, room, size, phase === Phase.Storm ? 1 : 0, period);
  }
}

function firepitOfRoom(s: State, room: number): number {
  for (let t = 0; t < TILES; t++) if (s.build[t] === Build.Firepit + 1 && s.rooms[t] === room) return t;
  return 0;
}

function rollIllness(s: State): void {
  for (let i = 0; i < s.n; i++) {
    if (!s.alive[i] || s.ill[i]) continue;
    const weight = s.mode.illnessBase + ((s.cold[i] + s.tired[i]) * s.mode.illnessWeight) / 100;
    if (below(s.streams.illness, 10000) < weight) {
      s.ill[i] = 1;
      emit(s, Ev.Ill, i, 0, 0, 0);
      raise(s, `ill${i}`, `${s.name[i]} has fallen ill`, s.pos[i], 3);
    }
  }
}

function dayBreak(s: State): void {
  const day = (s.tick / TICKS_PER_DAY) | 0;
  if (s.mode.regrow) {
    for (let t = 0; t < TILES; t++) if (s.regrow[t] > 0 && s.regrow[t] <= s.tick) s.regrow[t] = 0;
  }
  if (s.mode.arrivals.includes(day) && s.n < 16) {
    const beds = countBuild(s, Build.Bed);
    if (beds > livingCount(s) && stockOf(s, Res.Meal) >= 4) {
      s.pendingArrival = s.n;
      emit(s, Ev.Arrive, s.n, day, 0, 0);
      raise(s, `arrive${day}`, 'Someone has come out of the snow', homeTile(s), 2);
    }
  }
}

function countBuild(s: State, b: Build): number {
  let n = 0;
  for (let t = 0; t < TILES; t++) if (s.build[t] === b + 1) n++;
  return n;
}

/** The Crossing's fourth-day dusk: the south goes under, for good. */
function abandonTheSouth(s: State): void {
  for (let t = 0; t < TILES; t++) {
    if (rowOf(t) <= CROSSING_ABANDON_ROW) continue;
    if (s.rooms[t] !== OUTDOOR) continue;
    s.depth[t] = Math.min(MAX_DEPTH, s.depth[t] + 2);
  }
  for (let t = 0; t < TILES; t++) {
    if (rowOf(t) > CROSSING_ABANDON_ROW && s.build[t] === Build.Firepit + 1) s.build[t] = 0;
  }
  costChanged(s);
  raise(s, 'abandon', 'The south is under. The fire there is out.', -1, 4);
}

function checkSeverance(s: State): void {
  const home = homeTile(s);
  const f = fieldFrom(s, home);
  let cut = 0;
  for (let t = 0; t < TILES; t++) {
    if (s.build[t] === 0) continue;
    if (s.build[t] === Build.Wall + 1) continue;
    if (f[t] >= INF) cut++;
  }
  if (cut > 0 && s.severOpen === 0) {
    s.severOpen = s.tick;
    emit(s, Ev.Sever, cut, 0, 0, 0);
    raise(s, 'sever', `${cut} building${cut === 1 ? '' : 's'} cut off from the larder`, -1, 4);
  } else if (cut === 0 && s.severOpen > 0) {
    const dur = s.tick - s.severOpen;
    s.severedTicks += dur;
    if (dur > s.worstSeverance) s.worstSeverance = dur;
    s.severOpen = 0;
    emit(s, Ev.Restore, dur, 0, 0, 0);
    clearAlert(s, 'sever');
  }
}

/** How many buildings are currently unreachable from the main store — the HUD's `chain N`. */
export function severedCount(s: State): number {
  const f = fieldFrom(s, homeTile(s));
  let cut = 0;
  for (let t = 0; t < TILES; t++) {
    if (s.build[t] === 0 || s.build[t] === Build.Wall + 1) continue;
    if (f[t] >= INF) cut++;
  }
  return cut;
}

// ── a colonist's turn ───────────────────────────────────────────────────────

function advance(s: State, who: number): void {
  // mid-step
  if (s.moveUntil[who] > s.tick) return;
  if (s.moveTo[who] >= 0 && s.moveUntil[who] <= s.tick && s.moveTo[who] !== s.pos[who]) {
    s.pos[who] = s.moveTo[who];
    s.traversal[s.pos[who]]++;
    s.walked[who]++;
    s.moveTo[who] = -1;
  }

  if (s.ill[who]) return; // ill colonists do no work — they still eat, and still get cold

  // eating outranks everything, including a player call
  if (s.hunger[who] >= EAT_AT) {
    if (eatStep(s, who)) return;
  }

  // Going to the fire when frozen outranks work, and it is what makes a dead hearth lethal rather
  // than merely sad: a colonist who walks into an unheated room gets no warmer, and the illness roll
  // is weighted by exactly this number.
  if (s.cold[who] >= WARM_AT) {
    const warm = nearestWarmTile(s, who);
    if (warm >= 0) {
      if (s.pos[who] !== warm) {
        release(s, who, false);
        if (walkToward(s, who, warm)) return;
      } else if (s.cold[who] > COLD_OK) {
        release(s, who, false);
        return; // thawing out; needs() is draining cold
      }
    }
  }

  // a one-shot player call outranks the queue
  if (s.callTo[who] >= 0) {
    if (s.pos[who] === s.callTo[who]) {
      s.callTo[who] = -1;
    } else if (!walkToward(s, who, s.callTo[who])) {
      s.callTo[who] = -1;
    } else return;
  }

  // carrying something? deliver it before taking new work
  if (s.carryN[who] > 0 && s.stage[who] !== Stage.Fetch && s.stage[who] !== Stage.Travel) {
    if (deliverStep(s, who)) return;
  }

  // sleep when spent and a bed is reachable
  if (s.tired[who] >= SLEEP_AT && s.job[who] < 0) {
    const bed = nearestBuild(s, who, Build.Bed);
    if (bed >= 0) {
      if (s.pos[who] !== bed) {
        if (walkToward(s, who, bed)) return;
      } else return; // asleep; needs() drains tired
    }
  }

  if (s.job[who] < 0) {
    // A DECISION POINT. This is the only place a job is chosen, and the only place M5 looks.
    //
    // An idle colonist backs off for ten ticks rather than re-deciding every one of them. Without it
    // six idle colonists emitted ten thousand decision events in three game-days — which is not just
    // waste, it would swamp M5's violation count with noise and make the audit's own cost the thing
    // that decides whether the suite can run.
    if (s.stickyUntil[who] > s.tick) return;
    const cand = countCandidates(s, who);
    const pick = chooseJob(s, who);
    emit(s, Ev.Decision, who, cand, pick, 0);
    if (pick < 0) {
      s.stickyUntil[who] = s.tick + 10;
      return;
    }
    const j = jobById(s, pick);
    if (!j) return;
    j.claimedBy = who;
    s.job[who] = pick;
    s.stage[who] = Stage.Fetch;
    s.stickyUntil[who] = s.tick + 30;
    emit(s, Ev.Claim, who, j.id, j.cls, fieldFrom(s, s.pos[who])[j.tile]);
    return;
  }

  const j = jobById(s, s.job[who]);
  if (!j || j.done) {
    release(s, who, false);
    return;
  }
  runJob(s, who, j);
}

function nearestBuild(s: State, who: number, b: Build): number {
  const f = fieldFrom(s, s.pos[who]);
  let best = -1;
  let bestD = INF;
  for (let t = 0; t < TILES; t++) {
    if (s.build[t] !== b + 1) continue;
    if (f[t] >= INF) continue;
    if (b === Build.Bed && occupied(s, t, who)) continue;
    if (f[t] < bestD) {
      bestD = f[t];
      best = t;
    }
  }
  return best;
}

/** The nearest tile inside a heated room. Rooms, never radii. */
function nearestWarmTile(s: State, who: number): number {
  const heated = heatedRooms(s);
  if (heated.size === 0) return -1;
  const f = fieldFrom(s, s.pos[who]);
  let best = -1;
  let bestD = INF;
  for (let t = 0; t < TILES; t++) {
    if (!heated.has(s.rooms[t])) continue;
    if (f[t] >= INF) continue;
    if (f[t] < bestD) {
      bestD = f[t];
      best = t;
    }
  }
  return best;
}

function occupied(s: State, tile: number, except: number): boolean {
  for (let i = 0; i < s.n; i++) if (i !== except && s.alive[i] && s.pos[i] === tile) return true;
  return false;
}

/**
 * Eating is TWO transactions — a withdraw into the hands, then the meal itself. A single store->void
 * would make M3 ("every meal eaten was physically carried from a store to a mouth by a named
 * person") unsatisfiable, and would make the consumption stage of the supply chain a fiction.
 */
function eatStep(s: State, who: number): boolean {
  if (s.carryRes[who] === Res.Meal && s.carryN[who] > 0) {
    s.carryN[who] -= 1;
    if (s.carryN[who] === 0) s.carryRes[who] = -1;
    s.hunger[who] = Math.max(0, s.hunger[who] - MEAL_VALUE);
    s.eaten++;
    emit(s, Ev.Eat, who, 1, 0, 0);
    return true;
  }
  if (s.carryN[who] > 0) return false; // hands full of something else; deliver first
  const ci = nearestWith(s, s.pos[who], Res.Meal, 1);
  if (ci < 0) {
    raise(s, 'nomeal', 'No meal within reach of a hungry colonist', s.pos[who], 4);
    return false;
  }
  clearAlert(s, 'nomeal');
  if (s.pos[who] !== s.cont[ci].tile) return walkToward(s, who, s.cont[ci].tile);
  withdraw(s, who, ci, Res.Meal, 1);
  return true;
}

function deliverStep(s: State, who: number): boolean {
  const ci = nearestStoreWithSpace(s, s.pos[who]);
  if (ci < 0) {
    // Nowhere reachable with room. It goes on the ground, and the snow will find it.
    spill(s, who, s.carryRes[who] as Res, s.carryN[who]);
    s.carryN[who] = 0;
    s.carryRes[who] = -1;
    return true;
  }
  if (s.pos[who] !== s.cont[ci].tile) return walkToward(s, who, s.cont[ci].tile);
  deposit(s, who, ci, s.carryRes[who] as Res, s.carryN[who]);
  s.hauled[who] += s.carryN[who];
  s.carryN[who] = 0;
  s.carryRes[who] = -1;
  return true;
}

function skillFor(s: State, who: number, k: Skill): number {
  return s.skillKind[who] === k ? s.skillLvl[who] : 1;
}

function runJob(s: State, who: number, j: Job): void {
  switch (j.cls) {
    case Cls.Clear:
      workAt(s, who, j, j.tile, clearTicks(s.depth[j.tile]), () => {
        s.depth[j.tile] = 0;
        s.fill[j.tile] = 0;
        s.desig[j.tile] = Desig.None;
        costChanged(s);
        emit(s, Ev.Cleared, j.tile, 0, 0, 0);
      });
      return;
    case Cls.Cut: {
      const isStand = s.terrain[j.tile] === Terrain.Stand;
      const lvl = skillFor(s, who, Skill.Forager);
      const ticks = Math.max(10, ((isStand ? CUT_TICKS : FORAGE_TICKS) / lvl) | 0);
      workAt(s, who, j, j.tile, ticks, () => {
        const res = isStand ? Res.Wood : Res.Food;
        const yieldN = isStand ? STAND_YIELD : FORAGE_YIELD;
        const take = Math.min(CARRY_CAP, yieldN);
        s.carryRes[who] = res;
        s.carryN[who] = take;
        s.produced[res] += yieldN;
        // The harvest event reports the WHOLE yield, and the spill immediately puts down what will
        // not fit. Reporting only `take` and then spilling the excess made the ledger claim a
        // colonist had set down two logs it had never picked up — the audit caught it as a run of
        // impossible deposits, which is exactly the shape of bug M1 exists to find.
        emit(s, Ev.Harvest, who, res, yieldN, j.tile);
        if (yieldN > take) spill(s, who, res, yieldN - take);
        if (s.mode.regrow) {
          s.regrow[j.tile] = s.tick + (isStand ? STAND_REGROW : FORAGE_REGROW);
        } else {
          s.terrain[j.tile] = Terrain.Open;
        }
        s.desig[j.tile] = Desig.None;
      });
      return;
    }
    case Cls.Haul: {
      const ci = j.arg;
      if (ci < 0 || ci >= s.cont.length || total(s.cont[ci]) <= 0 || buried(s, ci)) {
        j.done = true;
        release(s, who, false);
        return;
      }
      if (s.pos[who] !== s.cont[ci].tile) {
        if (!walkToward(s, who, s.cont[ci].tile)) release(s, who, true);
        return;
      }
      for (let r = 0; r < RES_COUNT; r++) {
        if (s.cont[ci].amt[r] > 0) {
          const take = Math.min(CARRY_CAP, s.cont[ci].amt[r]);
          s.cont[ci].amt[r] -= take;
          s.carryRes[who] = r;
          s.carryN[who] = take;
          emit(s, Ev.Withdraw, who, r, take, ci);
          break;
        }
      }
      j.done = true;
      emit(s, Ev.Complete, who, j.id, j.cls, 0);
      release(s, who, false);
      return;
    }
    case Cls.Cook:
      craftAt(s, who, j, Res.Meal, MEAL_IN_FOOD, MEAL_IN_WOOD, MEAL_OUT, MEAL_TICKS, Skill.Cook);
      return;
    case Cls.Tend:
      if (j.arg === -2) {
        craftAt(s, who, j, Res.Remedy, REMEDY_IN_FOOD, REMEDY_IN_WOOD, 1, REMEDY_TICKS, Skill.Healer);
      } else {
        tendStep(s, who, j);
      }
      return;
    case Cls.Build:
      buildStep(s, who, j);
      return;
    default:
      release(s, who, false);
  }
}

function workAt(s: State, who: number, j: Job, tile: number, ticks: number, done: () => void): void {
  if (s.pos[who] !== tile && !adjacentOrOn(s, who, tile)) {
    if (!walkToward(s, who, tile)) release(s, who, true);
    return;
  }
  if (s.stage[who] !== Stage.Work) {
    s.stage[who] = Stage.Work;
    s.workLeft[who] = ticks;
  }
  // Exhaustion is a real cost: above TIRED_SLOW_AT everything takes twice as long.
  s.workLeft[who] -= s.tired[who] > TIRED_SLOW_AT ? 1 : 2;
  if (s.workLeft[who] > 0) return;
  done();
  j.done = true;
  emit(s, Ev.Complete, who, j.id, j.cls, 0);
  release(s, who, false);
}

function craftAt(
  s: State,
  who: number,
  j: Job,
  out: Res,
  inFood: number,
  inWood: number,
  outN: number,
  baseTicks: number,
  skill: Skill,
): void {
  // Fetch the ingredients first, which is what makes the stove's supply route a real, failable stage.
  if (s.stage[who] === Stage.Fetch) {
    const needFood = inFood - (s.carryRes[who] === Res.Food ? s.carryN[who] : 0);
    if (needFood > 0) {
      const ci = nearestWith(s, s.pos[who], Res.Food, needFood);
      if (ci < 0) {
        release(s, who, true);
        return;
      }
      if (s.pos[who] !== s.cont[ci].tile) {
        if (!walkToward(s, who, s.cont[ci].tile)) release(s, who, true);
        return;
      }
      withdraw(s, who, ci, Res.Food, needFood);
      return;
    }
    s.stage[who] = Stage.Travel;
    return;
  }

  const lvl = skillFor(s, who, skill);
  const ticks = Math.max(20, (baseTicks / lvl) | 0);
  workAt(s, who, j, j.tile, ticks, () => {
    // The wood is taken from the nearest reachable store at the moment of the craft, so a stove with
    // no wood in reach simply cannot cook — which is the supply chain, not a special case.
    const wi = nearestWith(s, j.tile, Res.Wood, inWood);
    if (wi >= 0) {
      s.cont[wi].amt[Res.Wood] -= inWood;
      emit(s, Ev.Consume, -1, Res.Wood, inWood, wi);
    }
    // The fetched food is in this colonist's hands and is spent here. Declared, or M1 sees a unit
    // vanish and says so.
    if (s.carryRes[who] === Res.Food && s.carryN[who] > 0) {
      emit(s, Ev.Consume, who, Res.Food, s.carryN[who], j.tile);
    }
    s.carryN[who] = 0;
    s.carryRes[who] = out;
    s.carryN[who] = outN;
    s.produced[out] += outN;
    emit(s, Ev.Craft, j.tile, out, outN, who);
  });
}

function tendStep(s: State, who: number, j: Job): void {
  const patient = j.arg;
  if (patient < 0 || !s.alive[patient] || !s.ill[patient]) {
    j.done = true;
    release(s, who, false);
    return;
  }
  if (s.carryRes[who] !== Res.Remedy || s.carryN[who] <= 0) {
    const ci = nearestWith(s, s.pos[who], Res.Remedy, 1);
    if (ci < 0) {
      release(s, who, true);
      return;
    }
    if (s.pos[who] !== s.cont[ci].tile) {
      if (!walkToward(s, who, s.cont[ci].tile)) release(s, who, true);
      return;
    }
    withdraw(s, who, ci, Res.Remedy, 1);
    return;
  }
  const at = s.pos[patient];
  if (!adjacentOrOn(s, who, at)) {
    if (!walkToward(s, who, at)) release(s, who, true);
    return;
  }
  emit(s, Ev.Consume, who, Res.Remedy, s.carryN[who], at);
  s.carryN[who] = 0;
  s.carryRes[who] = -1;
  s.ill[patient] = 0;
  clearAlert(s, `ill${patient}`);
  emit(s, Ev.Recover, patient, 0, 0, 0);
  j.done = true;
  emit(s, Ev.Complete, who, j.id, j.cls, 0);
  release(s, who, false);
}

function buildStep(s: State, who: number, j: Job): void {
  const spec = BUILDS[j.arg as Build];
  if (!spec) {
    j.done = true;
    release(s, who, false);
    return;
  }
  if (spec.id === Build.Stockpile && countBuild(s, Build.Stockpile) >= MAX_STOCKPILES) {
    j.done = true;
    s.desig[j.tile] = Desig.None;
    release(s, who, false);
    return;
  }
  if (s.stage[who] === Stage.Fetch) {
    const have = s.carryRes[who] === Res.Wood ? s.carryN[who] : 0;
    if (have < spec.wood) {
      const ci = nearestWith(s, s.pos[who], Res.Wood, spec.wood - have);
      if (ci < 0) {
        release(s, who, true);
        return;
      }
      if (s.pos[who] !== s.cont[ci].tile) {
        if (!walkToward(s, who, s.cont[ci].tile)) release(s, who, true);
        return;
      }
      withdraw(s, who, ci, Res.Wood, spec.wood - have);
      return;
    }
    s.stage[who] = Stage.Travel;
    return;
  }
  const lvl = skillFor(s, who, Skill.Carpenter);
  const ticks = Math.max(15, (spec.ticks / lvl) | 0);
  workAt(s, who, j, j.tile, ticks, () => {
    if (s.carryRes[who] === Res.Wood && s.carryN[who] > 0) {
      emit(s, Ev.Consume, who, Res.Wood, s.carryN[who], j.tile);
    }
    s.carryN[who] = 0;
    s.carryRes[who] = -1;
    s.build[j.tile] = spec.id + 1;
    s.desig[j.tile] = Desig.None;
    if (spec.id === Build.Stockpile) s.cont.push(makeCont(0, j.tile));
    costChanged(s);
    emit(s, Ev.Built, j.tile, spec.id, 0, 0);
  });
}

// ── commands ────────────────────────────────────────────────────────────────

export function applyCommand(s: State, c: Command): void {
  switch (c.kind) {
    case Cmd.Designate: {
      const [t0, t1, kind, arg] = c.args;
      forRect(t0, t1, (t) => {
        if (kind === Desig.Clear && s.depth[t] === 0) return;
        if (kind === Desig.Cut && s.terrain[t] !== Terrain.Stand && s.terrain[t] !== Terrain.Forage) return;
        if (kind === Desig.Build && (s.build[t] !== 0 || s.terrain[t] === Terrain.Rock)) return;
        s.desig[t] = kind;
        s.desigArg[t] = arg;
      });
      regenJobs(s);
      return;
    }
    case Cmd.Cancel:
      forRect(c.args[0], c.args[1], (t) => {
        s.desig[t] = Desig.None;
      });
      regenJobs(s);
      return;
    case Cmd.Rank: {
      const order = c.args.slice(0, CLS_COUNT);
      const seen = new Set(order);
      if (seen.size !== CLS_COUNT) return;
      s.rank = order;
      // A re-rank invalidates nobody's current job — stickiness is what stops claim-thrash — but the
      // next decision point sees the new order.
      return;
    }
    case Cmd.Forbid: {
      const [who, cls, on] = c.args;
      if (who < 0 || who >= s.n) return;
      if (on) s.forbid[who] |= 1 << cls;
      else s.forbid[who] &= ~(1 << cls);
      return;
    }
    case Cmd.Call: {
      const [who, tile] = c.args;
      if (who < 0 || who >= s.n || !s.alive[who]) return;
      if (!passable(graphOf(s), tile)) return;
      s.callTo[who] = tile;
      release(s, who, false);
      return;
    }
    case Cmd.Arrival: {
      if (s.pendingArrival < 0) return;
      if (c.args[0] === 1) {
        addColonist(s, s.pendingArrival);
        s.arrivalsSeen++;
      }
      s.pendingArrival = -1;
      return;
    }
  }
}

function forRect(t0: number, t1: number, f: (t: number) => void): void {
  const c0 = Math.min(t0 % COLS, t1 % COLS);
  const c1 = Math.max(t0 % COLS, t1 % COLS);
  const r0 = Math.min((t0 / COLS) | 0, (t1 / COLS) | 0);
  const r1 = Math.max((t0 / COLS) | 0, (t1 / COLS) | 0);
  for (let r = r0; r <= r1; r++) for (let c = c0; c <= c1; c++) f(r * COLS + c);
}

// ── read-only helpers the UI and the tests share ────────────────────────────

export function dayOf(s: State): number {
  return ((s.tick / TICKS_PER_DAY) | 0) + 1;
}

export function quarterOf(s: State): number {
  return ((s.tick % TICKS_PER_DAY) / TICKS_PER_QUARTER) | 0;
}

export function phaseNow(s: State): Phase {
  return phaseAt(s.schedule, s.tick, TICKS_PER_QUARTER);
}

/** The next three quarters, for the forecast band. */
export function forecast(s: State): Phase[] {
  const q = (s.tick / TICKS_PER_QUARTER) | 0;
  const out: Phase[] = [];
  for (let i = 1; i <= 3; i++) out.push(s.schedule[Math.min(q + i, s.schedule.length - 1)] ?? Phase.Calm);
  return out;
}

/** The tile with the most traversals — the run summary's best sentence. */
export function chokepoint(s: State): number {
  let best = -1;
  let bestN = 0;
  for (let t = 0; t < TILES; t++) {
    if (s.traversal[t] > bestN) {
      bestN = s.traversal[t];
      best = t;
    }
  }
  return best;
}

export { LARDER_T, DOOR_T, CORRIDOR_T };
